import { useEffect, useContext, useMemo, useState, Fragment } from "react";
import { LoginContext } from "../../hooks/context/LoginContext";
import Menusup from "../../componentes/menus/menusup/Menusup";
import Sidebar from "../../componentes/menus/sidebar/Sidebar";
import Footer from "../../componentes/footer/Footer";
import Section from "../../componentes/ui/Section";
import PermissionRefused from "../../componentes/ui/PermissionRefused";
import LoadingPage from "../../componentes/ui/LoadingPage";
import { getTitle, getDecodedCookie } from "../../helpers/helperApp";

const MOCK_INSTALLATION_CONFIG = {
  signers: [
    { id: 1, label: "Firma 1" },
    { id: 2, label: "Firma 2" },
    { id: 3, label: "Firma 3" },
  ],
};

const MOCK_SIGNATURES = [
  {
    id: 1,
    order: "PC-2024-0042",
    description: "Repuestos motor principal - filtros y juntas",
    amount: 4250.09,
    status: "en_proceso",
    sentToSupplier: false,
    signatures: [
      { signerId: 1, status: "firmado" },
      { signerId: 2, status: "pendiente" },
      { signerId: 3, status: "pendiente" },
    ],
  },
  {
    id: 2,
    order: "PC-2024-0043",
    description: "Pinturas epoxy casco obra viva",
    amount: 11880.36,
    status: "rechazado",
    sentToSupplier: false,
    signatures: [
      { signerId: 1, status: "firmado" },
      { signerId: 2, status: "firmado" },
      { signerId: 3, status: "pendiente" },
    ],
  },
  {
    id: 3,
    order: "PC-2024-0041",
    description: "Equipos de navegación - actualización GPS",
    amount: 7320.03,
    status: "completo",
    sentToSupplier: true,
    signatures: [
      { signerId: 1, status: "firmado" },
      { signerId: 2, status: "firmado" },
      { signerId: 3, status: "firmado" },
    ],
  },
];

function Signatures(props) {
  const { validateSesion, socket, confApp, saveConfApp, getConfApp } = useContext(LoginContext);
  const [confPantalla, saveConfPantalla] = useState(confApp);
  const [loadedConf, setLoadedConf] = useState(false);
  const [tieneNivel, setTieneNivel] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [shipFilter, setShipFilter] = useState("todos");
  const [rows, setRows] = useState(MOCK_SIGNATURES);
  const [actionAlert, setActionAlert] = useState({ type: "", message: "" });
  const [showSignModal, setShowSignModal] = useState(false);
  const [signModalMessage, setSignModalMessage] = useState("");
  const signerColumns = useMemo(() => MOCK_INSTALLATION_CONFIG.signers ?? [], []);
  const requiredSignerIds = useMemo(() => signerColumns.map((signer) => signer.id), [signerColumns]);

  useEffect(() => {
    validateSesion(props)
      .then((respuesta) => {
        if (confPantalla === undefined) return getConfApp();

        document.title = getTitle(confPantalla.config.trad, "Firmas");
        setTieneNivel(respuesta == 1);
        setLoadedConf(true);
      })
      .then((respuesta) => {
        if (respuesta !== undefined) {
          saveConfPantalla(respuesta.msg);
          saveConfApp(respuesta.msg);
        }
      });

    return () => {
      if (socket != null) {
        socket.emit("salirPantalla", {
          idusu: getDecodedCookie("id"),
          tabla: "undefined",
        });
      }
      document.querySelector("body").classList.remove("sidebar-open");
    };
  }, [confPantalla]);

  const ships = useMemo(
    () => ["todos", ...new Set(rows.map((row) => row.ship).filter(Boolean))],
    [rows]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchSearch =
        !search.trim() ||
        `${row.order} ${row.provider} ${row.description}`.toLowerCase().includes(search.toLowerCase());

      const matchStatus = statusFilter === "todos" || row.status === statusFilter;
      const matchShip = shipFilter === "todos" || row.ship === shipFilter;

      return matchSearch && matchStatus && matchShip;
    });
  }, [rows, search, statusFilter, shipFilter]);

  const counters = useMemo(() => {
    const inProcess = rows.filter((r) => r.status === "en_proceso").length;
    const complete = rows.filter((r) => r.status === "completo").length;
    const sent = rows.filter((r) => r.sentToSupplier).length;
    const rejected = rows.filter((r) => r.status === "rechazado").length;
    return { inProcess, complete, sent, rejected };
  }, [rows]);

  const badge = (value) => {
    if (value === "firmado") return <span className="badge badge-success"></span>;
    if (value === "rechazado") return <span className="badge badge-danger"></span>;
    return <span className="badge badge-secondary"></span>;
  };

  const isSignatureCompleted = (signatureStatus) => signatureStatus === "completo" || signatureStatus === "firmado";

  const getSignatureStatus = (row, signerId) =>
    row.signatures?.find((signature) => signature.signerId === signerId)?.status ?? "pendiente";

  const allRequiredSignaturesComplete = (row) =>
    requiredSignerIds.every((signerId) => isSignatureCompleted(getSignatureStatus(row, signerId)));

  const handleSign = (rowId, signerId) => {
    const currentRow = rows.find((row) => row.id === rowId);
    setRows((previousRows) =>
      previousRows.map((row) => {
        if (row.id !== rowId) return row;

        const nextSignatures = row.signatures?.map((signature) =>
          signature.signerId === signerId ? { ...signature, status: "firmado" } : signature
        );

        return { ...row, signatures: nextSignatures };
      })
    );
    setSignModalMessage(
      `La firma ${signerId} del pedido ${currentRow?.order ?? rowId} se ha registrado correctamente.`
    );
    setShowSignModal(true);
  };

  const handleReject = (row) => {
    setActionAlert({
      type: "warning",
      message: `Pedido ${row.order}: acción de rechazo ejecutada (mock).`,
    });
  };

  const handleModify = (row) => {
    setActionAlert({
      type: "info",
      message: `Pedido ${row.order}: acción de modificación ejecutada (mock).`,
    });
  };

  const renderSignatureStatus = (signatureStatus) => {
    if (signatureStatus === "firmado" || signatureStatus === "completo") {
      return (
        <span className="btn btn-xs btn-outline-success disabled" style={{ minWidth: 88 }}>
          ✓ Firmado
        </span>
      );
    }

    return (
      <span className="btn btn-xs btn-outline-secondary disabled" style={{ minWidth: 88 }}>
        -- Firmar
      </span>
    );
  };

  return (
    <div className="App wrapper">
      {confPantalla !== undefined ? (
        <Fragment>
          <Menusup props={props} />
          <Sidebar props={props} />
        </Fragment>
      ) : null}

      <div className="content-wrapper">
        <section className="content">
          {!tieneNivel ? (
            <PermissionRefused traduction={"Acceso denegado"} />
          ) : loadedConf && confPantalla !== undefined ? (
            <Section
              titulo={"Pedidos pendientes de firma"}
              icono={"fas fa-signature"}
              col={12}
              idPrin={""}
              idHijo={""}
              getFirmas={undefined}
              showBackButton={false}>

              <div className="row mb-3">
                <div className="col-md-4">
                  <input className="form-control" placeholder="Buscar pedido" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="todos">Todos los estados</option>
                    <option value="en_proceso">En proceso</option>
                    <option value="completo">Completo</option>
                    <option value="rechazado">Rechazado</option>
                  </select>
                </div>
              </div>
              {actionAlert.message && (
                <div className={`alert alert-${actionAlert.type} py-2`} role="alert">
                  {actionAlert.message}
                </div>
              )}

              <div className="table-responsive">
                <table className="table table-sm table-hover table-striped">
                  <thead>
                    <tr>
                      <th>Nº Pedido</th>
                      <th>Descripción</th>
                      <th>Importe</th>  
                      {signerColumns.map((signer, index) => (
                        <th key={signer.id}>{signer.label ?? `Firma ${index + 1}`}</th>
                      ))}
                      <th>Acciones</th>
                      <th>Comparativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.order}</td>
                        <td>{row.description}</td>
                        <td>{row.amount.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €</td>
                        {signerColumns.map((signer) => {
                          const signatureStatus = getSignatureStatus(row, signer.id);
                          const canSign = signatureStatus === "pendiente";

                          return (
                            <td key={`${row.id}-${signer.id}`}>
                              {canSign ? (
                                <button
                                  className="btn btn-xs btn-outline-warning"
                                  onClick={() => handleSign(row.id, signer.id)}
                                  style={{ minWidth: 88 }}>
                                  ✍ Firmar
                                </button>
                              ) : (
                                renderSignatureStatus(signatureStatus)
                              )}
                            </td>
                          );
                        })}
                        <td>
                          <button className="btn btn-xs btn-outline-danger mr-1" onClick={() => handleReject(row)}>
                            Rechazar
                          </button>
                          <button className="btn btn-xs btn-outline-primary mr-1" onClick={() => handleModify(row)}>
                            Modificar
                          </button>
                          {allRequiredSignaturesComplete(row) && (
                            <>
                              <button className="btn btn-xs btn-outline-warning mr-1">Enviar a proveedor</button>
                              <button className="btn btn-xs btn-outline-secondary">Ver adjuntos</button>
                            </>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-xs btn-outline-primary mr-1"><i className="fal fa-eye"></i></button>
                        </td>
                      </tr>
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={signerColumns.length + 5} className="text-center text-muted">Sin resultados</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {showSignModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
                  <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Firma registrada</h5>
                        <button type="button" className="close" aria-label="Close" onClick={() => setShowSignModal(false)}>
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                      <div className="modal-body">
                        <p className="mb-0">{signModalMessage}</p>
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowSignModal(false)}>
                          Aceptar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showSignModal && <div className="modal-backdrop fade show"></div>}
            </Section>
          ) : (
            <LoadingPage />
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}

export default Signatures;